'use client';
import React, { useState, useEffect } from 'react';
import ConfirmationModal from '../../components/modals/confirmationModal';
import SendMessageModal from '../../components/modals/SendMessageModal';
import Senticon from '../../assets/icons/sent-msg.svg';
import usersIcon from '../../assets/icons/usersIcon.svg';
import documentIcon from '../../assets/icons/documentIcon.svg';
import StatCard from '../../components/shared/StatCard';
import AuditLogsTable from '../../components/shared/AuditLogsTable';
import UserQueriesTable from '../../components/shared/UserQueriesTable';
import { QueryService } from '../../services/QueryService';
import { useAuthContext } from '../../hooks/useAuth';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { AdminService } from '../../services/AdminService';

export default function SubAdminDashboard() {
	const styles = {
		tableDiv: 'bg-white rounded-lg overflow-auto mb-14',
		topTableHead: 'px-4 py-4 text-lg leading-100 font-medium text-gray text-left',
		tableData: 'px-4 py-4 text-lg font-medium text-dark-blue leading-100 text-left'
	};
	const { getUserDisplayName } = useAuthContext();

	const auditLogs = [
		{
			activity: 'Logged in',
			comment: 'Logged into the sub-admin',
			email: 'majidali@nesl-it.com',
			datetime: '12/10/2025, 2:30 PM'
		},
		{
			activity: 'Resolve query',
			comment: 'Resolved query #0981',
			email: 'muhammadhaseeb@nesl-it.com',
			datetime: '12/12/2025, 9:23 AM'
		},
		{
			activity: 'User Registration',
			comment: 'New user registered',
			email: 'alex.brown@example.com',
			datetime: '11/28/2025, 11:00 AM'
		},
		{
			activity: 'Document Deleted',
			comment: 'Document removed from system',
			email: 'lisa.davis@example.com',
			datetime: '11/22/2025, 3:30 PM'
		},
		{
			activity: 'Settings Updated',
			comment: 'System settings modified',
			email: 'admin@example.com',
			datetime: '10/25/2025, 1:15 PM'
		}
	];

	const [userQueries, setUserQueries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [documentCount, setDocumentCount] = useState(0);
	const [isLoadingData, setIsLoadingData] = useState(false);
	const [userCount, setUserCount] = useState(0);
	const [isDocsLoading, setIsDocsLoading] = useState(false);

	useEffect(() => {
		let isMounted = true;
		async function load() {
			try {
				setLoading(true);
				setError(null);
				const data = await QueryService.getQueries(1, 2);
				if (!isMounted) return;
				const mapped = (data?.queries || []).map((q) => ({
					query_id: `#${q.queryId}`,
					email: q?.createdBy?.email || '-',
					message: q?.query || '-',
					status: (q?.status || '').toUpperCase(),
					solution: q?.solution || null,
					__raw: q
				}));
				setUserQueries(mapped);
			} catch (err) {
				if (!isMounted) return;
				setError(err?.response?.data?.message || err?.message || 'Failed to load recent queries');
			} finally {
				if (!isMounted) return;
				setLoading(false);
			}
		}
		load();
		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		getUserCount(true);
		getDocumentCount(true);
	}, []);

	// Send flow state
	const [selectedQuery, setSelectedQuery] = useState(null);
	const [sendModalOpen, setSendModalOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [draftSolution, setDraftSolution] = useState('');

	const handleViewDetails = (query) => {
		setSelectedQuery(query);
		setDraftSolution(query.solution || '');
		setSendModalOpen(true);
	};
	// 1) Close compose modal, 2) open confirmation
	const handleSendMessage = (solutionText) => {
		setDraftSolution(solutionText);
		setSendModalOpen(false); // close first modal
		setConfirmOpen(true); // open "Message sent" modal
	};

	// Confirm -> call API to answer, then update UI on success
	const handleMarkAsSolved = async () => {
		if (!selectedQuery) return;
		try {
			const id = selectedQuery?.__raw?._id || selectedQuery?._id;
			if (!id) throw new Error('Missing query id');
			await QueryService.answerQuery(id, draftSolution);
			setUserQueries((prev) =>
				prev.map((q) =>
					q.query_id === selectedQuery.query_id
						? { ...q, status: 'SOLVED', solution: draftSolution }
						: q
				)
			);
			setConfirmOpen(false);
		} catch (e) {
			// Optionally show error inline or toast; for now, simple alert
			alert(e?.response?.data?.message || e?.message || 'Failed to submit answer');
		}
	};

	const getDocumentCount = async (status) => {
		setIsDocsLoading(true); // Set loading to true
		try {
			const res = await AdminService.getDocumentsCount(status);
			setDocumentCount(res.data.documentsCount);
		} finally {
			setIsDocsLoading(false); // Set loading to false
		}
	};

	const getUserCount = async (totalUsers) => {
		setIsLoadingData(true); // Set loading to true
		try {
			const res = await AdminService.getUsersCount(totalUsers);
			setUserCount(res.data.customersCount);
		} finally {
			setIsLoadingData(false); // Set loading to false
		}
	};

	return (
		<div className="">
			{/* Header Section */}
			<div className="flex flex-col items-center md:flex-row md:justify-between md:items-center gap-4 mb-10 md:mb-[50px]">
				<h1 className="text-2xl sm:text-3xl font-semibold text-dark-blue text-center md:text-left">
					Welcome, {getUserDisplayName()}
				</h1>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 mb-10 md:mb-[50px]">
				<StatCard
					icon={usersIcon}
					bgColor="bg-lightGrey"
					textColor="text-dark-blue"
					label="Total register users"
					value={userCount}
					newValue="5,541"
					onRefresh={() => getUserCount(true)}
					isRefreshing={isLoadingData}
				/>
				<StatCard
					icon={documentIcon}
					bgColor="bg-lightGrey"
					textColor="text-purple-600"
					label="Total projects"
					value={documentCount}
					newValue="14,543"
					onRefresh={() => getDocumentCount(true)}
					isRefreshing={isDocsLoading}
				/>
			</div>

			<AuditLogsTable heading={'Your Audit Logs'} auditLogs={auditLogs} />

			<div className="flex justify-between flex-col md:flex-row items-center mb-12">
				<div>
					<h1 className="text-[22px] font-medium text-dark-blue">Recent Users Queries</h1>
				</div>

				<div>
					<a
						href="user-queries"
						className="inline-flex items-center gap-2 text-dark-blue text-20 underline leading-24"
					>
						View all
					</a>
				</div>
			</div>
			{error ? (
				<div className="bg-white rounded-lg p-6 text-center text-red-600">{error}</div>
			) : loading ? (
				<div className="bg-white rounded-lg overflow-auto mb-14">
					<table className="min-w-[700px] xl:min-w-full">
						<thead className="bg-[#F2F3F7]">
							<tr>
								<th scope="col" className={styles.topTableHead}>
									QUERY ID
								</th>
								<th scope="col" className={styles.topTableHead}>
									EMAIL
								</th>
								<th scope="col" className={styles.topTableHead}>
									MESSAGE
								</th>
								<th scope="col" className={styles.topTableHead}>
									STATUS
								</th>
								<th scope="col" className={styles.topTableHead}>
									ACTIONS
								</th>
							</tr>
						</thead>
						<tbody className="bg-white">
							{Array.from({ length: 2 }).map((_, i) => (
								<tr key={`skeleton-row-${i}`}>
									<td className="px-4 py-4">
										<Skeleton width={90} height={20} />
									</td>
									<td className="px-4 py-4">
										<Skeleton width={220} height={20} />
									</td>
									<td className="px-4 py-4">
										<Skeleton width={420} height={20} />
									</td>
									<td className="px-5 py-4">
										<Skeleton width={80} height={28} />
									</td>
									<td className="px-5 py-4">
										<Skeleton width={90} height={20} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : (
				<UserQueriesTable userQueries={userQueries} onViewDetails={handleViewDetails} />
			)}
			<SendMessageModal
				isOpen={sendModalOpen}
				onClose={() => setSendModalOpen(false)}
				isImported={false}
				query={selectedQuery}
				onSend={handleSendMessage}
			/>

			{/* Message sent confirmation */}
			<ConfirmationModal
				isOpen={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				icon={<img src={Senticon} alt="Sent" className="mx-auto" />}
				heading="Mark ticket as resolved?"
				description={
					<p className="text-sm italic text-left text-gray-600">
						Submitting this response will mark the ticket as resolved and notify the requester.
					</p>
				}
				primaryButtonText="Mark as solved"
				secondaryButtonText="Cancel"
				onPrimaryClick={handleMarkAsSolved}
				onSecondaryClick={() => setConfirmOpen(false)}
			/>
		</div>
	);
}
